<template>
  <div>
    <div>
      <div class="page-bar">
        <breadcrumb :crumbs="breadcrumbArray" />
      </div>
      <h1 class="page-title">
        Roles
      </h1>
      <div class="note note-info">
        <p>Create and manage user roles.</p>
      </div>

      <!-- CREATE NEW START -->
      <div
        v-if="$root.permissions['create role']"
        class="portlet box blue-hoki margin-top-20"
      >
        <div
          class="portlet-title bg-blue-chambray"
          @click="toggleCreateRolePanel()"
        >
          <div class="caption">
            <i class="fa fa-plus-circle" />
            Create New Role
          </div>
          <div class="tools">
            <a :class="{'expand': !createCollapse, 'collapse': createCollapse}" />
          </div>
        </div>
        <div
          v-show="!createCollapse"
          class="portlet-body"
        >
          <form
            role="form"
            @submit.prevent="createRole()"
          >
            <div class="row">
              <div class="col-md-12">
                <div
                  v-show="createErrorMessage.length"
                  ref="createErrorMessage"
                  class="alert alert-danger"
                >
                  <button
                    class="close"
                    data-close="alert"
                    @click.prevent="clearCreateError()"
                  />
                  <span>{{ createErrorMessage }}</span>
                </div>
              </div>
              <div class="col-xs-12">
                <div class="form-group form-md-line-input form-md-floating-label">
                  <input
                    id="form_control_name"
                    ref="newRoleName"
                    v-model="newRole.name"
                    type="text"
                    class="form-control input-sm"
                    :class="{'edited': newRole.name.length}"
                  >
                  <label for="form_control_name">
                    Name
                  </label>
                </div>
                <el-tree
                  ref="newRoleTree"
                  :data="roleTree"
                  :highlight-current="true"
                  :props="{
                    'label': 'name',
                    'children': 'combined',
                    'disabled': 'disabled'
                  }"
                  :expand-on-click-node="true"
                  show-checkbox
                  @check-change="setNewRolePermission"
                >
                  <span
                    slot-scope="{ node, data }"
                    :class="{'module': data.module_id === undefined}"
                  >
                    {{ data.name }}
                  </span>
                </el-tree>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12">
                <button
                  type="submit"
                  class="btn blue pull-right"
                  :disabled="creating"
                >
                  Create
                  <i
                    v-show="creating"
                    class="fa fa-spinner fa-pulse fa-fw"
                  />
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <!-- CREATE NEW END -->

      <!-- SEARCH START -->
      <div
        v-if="roles.length"
        class="margin-top-20"
      >
        <div class="portlet box blue-hoki">
          <div
            class="portlet-title"
            @click="toggleSearchPanel()"
          >
            <div class="caption">
              <i class="fa fa-search" />
              Search Panel
            </div>
            <div class="tools">
              <a :class="{'expand': !searchCollapse, 'collapse': searchCollapse}" />
            </div>
          </div>
          <div
            v-show="!searchCollapse"
            class="portlet-body"
          >
            <form
              role="form"
            >
              <div class="form-body row">
                <div class="col-md-6">
                  <div class="form-group form-md-line-input form-md-floating-label">
                    <input
                      ref="search"
                      v-model="searchTerm"
                      type="text"
                      class="form-control input-sm"
                      :class="{'edited': searchTerm.length}"
                    >
                    <label for="search_options_search">
                      Search
                    </label>
                    <span
                      v-if="searchTerm.length && !filteredResults.length"
                      class="help-block persist"
                    >
                      No matching results.
                    </span>
                    <span
                      v-else
                      class="help-block persist"
                    >
                      Search by name.
                    </span>
                  </div>
                  <button
                    type="button"
                    class="btn btn-default pull-right"
                    @click="resetSearch()"
                  >
                    Reset Search
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <!-- SEARCH END -->

      <!-- LIST START -->
      <loading-screen
        :show="loading"
        :color="'#2C3E50'"
        :display="'inline'"
      />

      <div v-if="!loading && filteredResults.length">
        <div class="portlet light portlet-fit bordered margin-top-20">
          <div class="portlet-title bg-blue-chambray">
            <div class="menu-image-main">
              <img src="@/../public/client_logo.png">
            </div>
            <div class="caption">
              <span class="caption-subject font-default bold uppercase">
                Roles
              </span>
              <div class="caption-desc font-grey-cascade">
                Click on the edit button to edit a role and change the permissions it contains.
              </div>
            </div>
          </div>
          <div class="portlet-body">
            <div
              v-if="roles.length"
              class="clearfix margin-bottom-10"
            >
              <el-dropdown
                trigger="click"
                size="mini"
                :show-timeout="50"
                :hide-timeout="50"
                @command="updateSortByOrder"
              >
                <el-button size="mini">
                  Sort by
                  <span>
                    <i
                      v-if="sortBy.order === 'ASC'"
                      class="fa fa-sort-alpha-asc"
                    />
                    <i
                      v-if="sortBy.order === 'DESC'"
                      class="fa fa-sort-alpha-desc"
                    />
                  </span>
                  <i class="el-icon-arrow-down el-icon--right" />
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="ASC">
                    <i class="fa fa-sort-alpha-asc" />
                  </el-dropdown-item>
                  <el-dropdown-item command="DESC">
                    <i class="fa fa-sort-alpha-desc" />
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <page-results
                class="pull-right"
                :total-results="filteredResults.length"
                :active-page="activePage"
                @pageResults="pageResultsUpdate"
              />
            </div>
            <div class="mt-element-list">
              <div class="mt-list-container list-news">
                <ul>
                  <li
                    v-for="role in currentActivePageItems"
                    :id="'role-' + role.id"
                    :key="role.id"
                    class="mt-list-item actions-at-left margin-top-15"
                    :class="{'animated' : animated === `role-${role.id}`}"
                  >
                    <div class="list-item-actions">
                      <el-tooltip
                        v-if="$root.permissions['update role']"
                        content="Edit"
                        effect="light"
                        placement="right"
                      >
                        <a
                          class="btn btn-circle btn-icon-only btn-default"
                          @click="editRole(role)"
                        >
                          <i
                            class="fa fa-pencil"
                            aria-hidden="true"
                          />
                        </a>
                      </el-tooltip>
                      <el-tooltip
                        v-if="$root.permissions['create role'] && !$root.permissions['update role']"
                        content="View"
                        effect="light"
                        placement="right"
                      >
                        <a
                          class="btn btn-circle btn-icon-only btn-default"
                          @click="editRole(role)"
                        >
                          <i
                            class="fa fa-eye"
                            aria-hidden="true"
                          />
                        </a>
                      </el-tooltip>
                      <el-tooltip
                        v-if="$root.permissions['delete role']"
                        content="Delete"
                        effect="light"
                        placement="right"
                      >
                        <a
                          class="btn btn-circle btn-icon-only btn-default"
                          @click="openDeleteModal(role)"
                        >
                          <i
                            class="fa fa-trash"
                            aria-hidden="true"
                          />
                        </a>
                      </el-tooltip>
                    </div>
                    <div class="list-datetime bold font-red">
                      <span>{{ role.name }}</span>
                    </div>
                  </li>
                </ul>
              </div>
              <div
                v-if="roles.length && numPages > 1"
                class="clearfix"
              >
                <pagination
                  :passed-page="activePage"
                  :num-pages="numPages"
                  @activePageChange="activePageUpdate"
                />
              </div>
            </div>
            <div
              v-show="listErrorMessage.length"
              ref="listErrorMessage"
              class="alert alert-danger"
            >
              <button
                class="close"
                data-close="alert"
                @click="clearListError()"
              />
              <span>{{ listErrorMessage }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!filteredResults.length && !loading">
        <no-results
          :show="!roles.length"
          :type="'roles'"
        />
      </div>
    </div>
    <!-- LIST END -->

    <!-- EDIT MODAL START -->
    <modal
      ref="editModal"
      :width="900"
      :show="showEditRoleModal"
      effect="fade"
      @closeOnEscape="closeEditRoleModal"
    >
      <div
        slot="modal-header"
        class="modal-header"
      >
        <button
          type="button"
          class="close"
          @click="closeEditRoleModal()"
        >
          <span>&times;</span>
        </button>
        <h4 class="modal-title center">
          Edit Role
        </h4>
      </div>
      <div
        slot="modal-body"
        class="modal-body"
      >
        <div
          v-show="editErrorMessage.length"
          ref="editErrorMessage"
          class="alert alert-danger"
        >
          <button
            class="close"
            data-close="alert"
            @click="clearEditError()"
          />
          <span>{{ editErrorMessage }}</span>
        </div>
        <div class="form-group form-md-line-input form-md-floating-label">
          <input
            id="form_control_edited_name"
            v-model="roleToEdit.name"
            type="text"
            class="form-control input-sm"
            :class="{'edited': roleToEdit.name.length}"
          >
          <label for="form_control_edited_name">
            Name
          </label>
        </div>
        <el-tree
          v-if="showEditRoleModal"
          ref="editedRoleTree"
          :data="roleTree"
          :highlight-current="true"
          :props="{'label': 'name', 'children': 'combined', 'disabled': 'disabled'}"
          :expand-on-click-node="true"
          show-checkbox
          node-key="nodeId"
          :default-checked-keys="roleToEdit.previouslySelected"
          @check-change="setEditedRolePermission"
        >
          <span
            slot-scope="{ node, data }"
            :class="{'module': data.module_id === undefined}"
          >
            {{ data.name }}
          </span>
        </el-tree>
      </div>
      <div
        slot="modal-footer"
        class="modal-footer"
      >
        <button
          v-if="$root.permissions['create role'] && !$root.permissions['update role']"
          type="button"
          class="btn btn-primary"
          @click="closeEditRoleModal()"
        >
          Close
        </button>
        <button
          v-else
          type="button"
          class="btn btn-primary"
          :disabled="updating"
          @click="updateRole()"
        >
          Save
          <i
            v-show="updating"
            class="fa fa-spinner fa-pulse fa-fw"
          />
        </button>
      </div>
    </modal>
    <!-- EDIT MODAL END -->

    <!-- DELETE MODAL START -->
    <modal
      :show="showDeleteModal"
      effect="fade"
      @closeOnEscape="closeDeleteModal"
    >
      <div
        slot="modal-header"
        class="modal-header"
      >
        <button
          type="button"
          class="close"
          @click="closeDeleteModal()"
        >
          <span>&times;</span>
        </button>
        <h4 class="modal-title center">
          Delete Role
        </h4>
      </div>
      <div
        slot="modal-body"
        class="modal-body"
      >
        <div
          v-show="deleteErrorMessage.length"
          ref="deleteErrorMessage"
          class="alert alert-danger"
        >
          <button
            class="close"
            data-close="alert"
            @click="clearDeleteError()"
          />
          <span>{{ deleteErrorMessage }}</span>
        </div>
        <p>
          Do you want to delete
          <strong>{{ roleToDelete.name }}</strong>?
        </p>
      </div>
      <div
        slot="modal-footer"
        class="modal-footer"
      >
        <button
          type="button"
          class="btn btn-primary"
          :disabled="deleting"
          @click="deleteRole()"
        >
          Delete
          <i
            v-show="deleting"
            class="fa fa-spinner fa-pulse fa-fw"
          />
        </button>
      </div>
    </modal>
    <!-- DELETE MODAL END -->
  </div>
</template>

<script>
import Breadcrumb from '../../modules/Breadcrumb'
import LoadingScreen from '../../modules/LoadingScreen'
import NoResults from '../../modules/NoResults'
import RolesFunctions from '../../../controllers/Roles'
import Modal from '../../modules/Modal'
import Pagination from '../../modules/Pagination'
import PageResults from '../../modules/PageResults'
import ajaxErrorHandler from '../../../controllers/ErrorController'
import ModulesFunctions from '../../../controllers/Modules'
import { mapGetters, mapMutations } from 'vuex'

export default {
  components: {
    Breadcrumb,
    NoResults,
    LoadingScreen,
    Modal,
    Pagination,
    PageResults
  },
  data () {
    return {
      breadcrumbArray: [
        { name: 'Admin Manager', link: false },
        { name: 'Roles', link: false }
      ],
      createCollapse: true,
      creating: false,
      createErrorMessage: '',
      newRole: {
        name: '',
        guard_name: 'admin',
        permissions: []
      },
      editErrorMessage: '',
      updating: false,
      roleToEdit: {
        name: '',
        guard_name: 'admin',
        permissions: []
      },
      loading: false,
      roles: [],
      showEditRoleModal: false,
      showDeleteModal: false,
      deleteErrorMessage: '',
      deleting: false,
      roleToDelete: {},
      animated: '',
      searchCollapse: true,
      searchError: '',
      searchTerm: '',
      activePage: 1,
      resultsPerPage: 25,
      sortBy: {
        order: 'ASC'
      },
      roleTree: [],
      cleanRoleTree: [],
      modules: []
    }
  },
  computed: {
    numPages () {
      return Math.ceil(this.filteredResults.length / this.resultsPerPage)
    },
    currentActivePageItems () {
      return this.userSort(this.filteredResults).slice(
        this.resultsPerPage * (this.activePage - 1),
        this.resultsPerPage * (this.activePage - 1) +
          this.resultsPerPage
      )
    },
    filteredResults () {
      return this.roles.filter(permission => (
        permission.name
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase())
      ))
    },
    ...mapGetters(['can'])
  },
  created () {
    this.getRoles()
    this.getModules()
  },
  methods: {
    /**
     * To get a list of brand admins.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    getModules () {
      this.loading = true
      this.clearListError()
      var rolesVue = this
      return ModulesFunctions.getFullModules()
        .then(response => {
          if (response.code === 200 && response.status === 'ok') {
            rolesVue.loading = false
            rolesVue.modules = response.payload
            rolesVue.createPermissionTree()
          } else {
            rolesVue.loading = false
          }
        })
        .catch(reason => {
          rolesVue.loading = false
          ajaxErrorHandler({
            reason,
            errorText: 'Could not get modules',
            errorName: 'listErrorMessage',
            vue: rolesVue
          })
        })
    },
    /**
     * To create the data object for <el-tree>
     * @function
     * @returns {array} - An array of nested nodes
     */
    createPermissionTree () {
      let role = this.listToTree(
        this.modules,
        this.modules.filter(mod => mod.parent_module === 0)[0]
      )

      for (let mod of role) {
        this.combinePermissionsAndModules(mod)
      }

      this.cleanRoleTree = role
      this.roleTree = role
    },
    /**
     * To convert a flat list of nodes into a tree.
     * @function
     * @param {array} arr - The flat array
     * @param {parent} parent - The root node
     * @returns {array} - An array of nested nodes
     */
    listToTree (arr, parent) {
      try {
        let tree = []
        let mappedArr = {}
        let arrElem
        let mappedElem

        // First map the nodes of the array to an object -> create a hash table.
        for (var i = 0, len = arr.length; i < len; i++) {
          arrElem = arr[i]
          mappedArr[arrElem.id] = arrElem
        }

        for (var id in mappedArr) {
          if (mappedArr.hasOwnProperty(id)) {
            mappedElem = mappedArr[id]
            // If the element is not at the root level, add it to its parent array of children.
            if (mappedElem.parent_module) {
              mappedArr[mappedElem['parent_module']]['sub_modules'].push(mappedElem)
              // If the element is at the root level, add it to first level elements array.
            } else {
              mappedElem.disabled =
                this.$root.permissions['update role'] ===
                undefined
              tree.push(mappedElem)
            }
          }
        }
        return tree
      } catch (e) {}
    },
    /**
     * To combine permissions and sub_modules into a field for the tree component to display
     * @function
     * @param {object} current - The tree node to process
     * @returns {undefined}
     */
    combinePermissionsAndModules (current) {
      try {
        const _this = this
        let ownPermissions = current.permissions.filter(
          permission => this.$root.permissions[permission.name]
        )
        current.combined = [...ownPermissions, ...current.sub_modules]
        current.combined = current.combined.map(item => {
          item.nodeId =
            item.module_id === undefined
              ? `m${item.id}`
              : `p${item.id}`

          if (this.can('sync permissions for role')) {
            item.disabled = false
          } else {
            if (this.roleToEdit.permissions.includes(item.id)) {
              if (this.can('revoke permissions from role')) {
                item.disabled = false
              } else {
                item.disabled = true
              }
            } else {
              if (this.can('assign permissions to role')) {
                item.disabled = false
              } else {
                item.disabled = true
              }
            }
          }
          return item
        })
        current.disabled = current.combined.length === 0

        var children = current.sub_modules
        for (var i = 0; i < children.length; i++) {
          _this.combinePermissionsAndModules(children[i])
        }
      } catch (e) {}
    },
    /**
     * To update the permissions selection when editing a role
     * @function
     * @returns {undefined}
     */
    setEditedRolePermission () {
      this.roleToEdit.permissions = this.$refs.editedRoleTree
        .getCheckedNodes()
        .filter(node => node.module_id !== undefined)
        .map(node => node.id)
    },
    /**
     * To update the permissions selection when creating a role
     * @function
     * @returns {undefined}
     */
    setNewRolePermission () {
      this.newRole.permissions = this.$refs.newRoleTree
        .getCheckedNodes()
        .filter(node => node.module_id !== undefined)
        .map(node => node.id)
    },
    /**
     * To update permissions based on user's selection
     * @function
     * @param {array} permissions - An array of permission ids
     * @returns {undefined}
     */
    updateNewRolePermissions (permissions) {
      this.newRole.permissions = permissions
    },
    /**
     * To update permissions based on user's selection
     * @function
     * @param {array} permissions - An array of permission ids
     * @returns {undefined}
     */
    updateEditedRolePermissions (permissions) {
      this.roleToEdit.permissions = [...permissions]
    },
    /**
     * To format a phone number
     * @function
     * @param {string} phone - The phone number to format
     * @returns {string} The formatted phone string
     */
    formatPhone (phone) {
      try {
        let digits = phone.replace(/\D/g, '')
        return (
          digits.slice(0, 3) +
          '-' +
          digits.slice(3, 6) +
          '-' +
          digits.slice(6)
        )
      } catch (err) {
        return ''
      }
    },
    /**
     * To update the order property of sortBy.
     * @function
     * @param {object} value - The new value to assign.
     * @returns {undefined}
     */
    updateSortByOrder (value) {
      this.sortBy.order = value
      this.activePageUpdate(1)
    },
    /**
     * To sort the orders list.
     * @function
     * @param {array} orders - The array of orders.
     * @returns {array} - The sorted array of orders
     */
    userSort (orders) {
      let input = orders
      function asc (a, b) {
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
          return -1
        } else if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1
        } else {
          if (a.id > b.id) {
            return -1
          } else if (a.id < b.id) {
            return 1
          } else {
            return 0
          }
        }
      }

      function desc (a, b) {
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return -1
        } else if (a.name.toLowerCase() < b.name.toLowerCase()) {
          return 1
        } else {
          if (a.id > b.id) {
            return -1
          } else if (a.id < b.id) {
            return 1
          } else {
            return 0
          }
        }
      }

      if (this.sortBy.order === 'ASC') {
        return input.sort(asc)
      } else {
        return input.sort(desc)
      }
    },
    /**
     * To catch updates from the PageResults component when the number of page results is updated.
     * @function
     * @param {integer} val - The number of page results to be returned.
     * @returns {undefined}
     */
    pageResultsUpdate (val) {
      if (parseInt(this.resultsPerPage) !== parseInt(val)) {
        this.resultsPerPage = val
        this.activePageUpdate(1)
      }
    },
    /**
     * To update the currently active pagination page.
     * @function
     * @param {integer} val - An integer representing the page number that we are updating to.
     * @returns {undefined}
     */
    activePageUpdate (val) {
      if (parseInt(this.activePage) !== parseInt(val)) {
        this.activePage = val
      }
    },
    /**
     * To toggle the search panel
     * @function
     * @returns {undefined}
     */
    toggleSearchPanel () {
      if (!this.searchTerm) {
        this.searchCollapse = !this.searchCollapse
      }
      this.$nextTick(function () {
        if (!this.searchCollapse) {
          this.$refs.search.focus()
        }
      })
    },
    /**
     * To clear the current search error.
     * @function
     * @returns {undefined}
     */
    clearSearchError () {
      this.searchError = ''
    },
    /**
     * To clear the current search criteria.
     * @function
     * @returns {undefined}
     */
    resetSearch () {
      this.searchTerm = ''
    },
    /**
     * To display the edit modal
     * @function
     * @param {object} role - The role object to be edited
     * @returns {undefined}
     */
    editRole (role) {
      let rolesVue = this
      this.listRolePermissions(role)
        .then(permissions => {
          rolesVue.roleToEdit = {
            ...role,
            permissions,
            previouslySelected: permissions.map(id => `p${id}`)
          }
          this.createPermissionTree()
          rolesVue.showEditRoleModal = true
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not get role details',
            errorName: 'listErrorMessage',
            vue: rolesVue
          })
        })
    },
    /**
     * To fetch permissions for a role
     * @function
     * @param {object} role - The role to fetch permissions for
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    listRolePermissions (role) {
      return RolesFunctions.listRolePermissions(role)
        .then(response => {
          return response.payload.permissions.map(
            permission => permission.id
          )
        })
        .catch(reason => {
          return []
        })
    },
    /**
     * To close the edit modal
     * @function
     * @returns {undefined}
     */
    closeEditRoleModal () {
      this.clearEditError()
      this.showEditRoleModal = false
    },
    /**
     * To get a list of brand admins.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    getRoles () {
      this.loading = true
      this.clearListError()
      var rolesVue = this
      return RolesFunctions.getRoles()
        .then(response => {
          if (response.code === 200 && response.status === 'ok') {
            rolesVue.loading = false
            if (this.$root.roles.some(role => role.name === 'super admin')) {
              rolesVue.roles = response.payload
            } else {
              rolesVue.roles = response.payload.filter(
                role => role.name !== 'super admin'
              )
            }
          } else {
            rolesVue.loading = false
          }
        })
        .catch(reason => {
          rolesVue.loading = false
          ajaxErrorHandler({
            reason,
            errorText: 'Could not get roles',
            errorName: 'listErrorMessage',
            vue: rolesVue
          })
        })
    },
    /**
     * To get a list of brand admins.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    createRole () {
      var rolesVue = this

      return this.validateNewRoleData()
        .then(response => {
          rolesVue.creating = true
          rolesVue.clearCreateError()
          return RolesFunctions.createRole(rolesVue.newRole)
            .then(response => {
              rolesVue.getRoles()
              rolesVue.resetCreateForm()
              rolesVue.showCreateSuccess()
            })
            .catch(reason => {
              ajaxErrorHandler({
                reason,
                errorText: 'Could not create role',
                errorName: 'createErrorMessage',
                vue: rolesVue
              })
            })
            .finally(() => {
              rolesVue.creating = false
            })
        })
        .catch(reason => {
          rolesVue.createErrorMessage = reason
          rolesVue.$scrollTo(
            rolesVue.$refs.createErrorMessage,
            1000,
            {
              offset: -50
            }
          )
        })
    },
    /**
     * To reset the create new form.
     * @function
     * @returns {undefined}
     */
    resetCreateForm () {
      this.createCollapse = true
      this.newRole = {
        name: '',
        guard_name: 'admin',
        permissions: []
      }
      this.roleTree = [...this.cleanRoleTree]
    },
    /**
     * To reset the create new form.
     * @function
     * @returns {undefined}
     */
    resetEditForm () {
      this.roleToEdit = {
        name: '',
        guard_name: 'admin',
        permissions: []
      }
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showCreateSuccess (payload = {}) {
      let title = 'Success'
      let text = 'The Role has been created'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The Role has been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showEditSuccess (payload = {}) {
      let title = 'Success'
      let text = 'The Role has been saved'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The changes have been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    },
    /**
     * To toggle the create new panel.
     * @function
     * @returns {undefined}
     */
    toggleCreateRolePanel () {
      this.createCollapse = !this.createCollapse
      this.$nextTick(function () {
        if (!this.createCollapse) {
          this.$refs.newRoleName.focus()
        }
      })
    },
    /**
     * To clear the current error.
     * @function
     * @returns {undefined}
     */
    clearCreateError () {
      this.createErrorMessage = ''
    },
    /**
     * To clear the current error.
     * @function
     * @returns {undefined}
     */
    clearEditError () {
      this.editErrorMessage = ''
    },
    /**
     * To clear the current error.
     * @function
     * @returns {undefined}
     */
    clearListError () {
      this.listErrorMessage = ''
    },
    /**
     * To update a role.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    updateRole () {
      var rolesVue = this

      return this.validateEditedRoleData()
        .then(response => {
          rolesVue.updating = true
          rolesVue.clearEditError()
          return RolesFunctions.updateRole(rolesVue.roleToEdit)
            .then(response => {
              rolesVue.closeEditRoleModal()
              rolesVue.refreshUserPermissions(response.payload)
              rolesVue.showEditSuccess(response.payload)
              for (var i = 0; i < rolesVue.roles.length; i++) {
                if (
                  rolesVue.roles[i].id ===
                  rolesVue.roleToEdit.id
                ) {
                  rolesVue.roles[i].name =
                    response.payload.name
                }
              }
              rolesVue.animated = `role-${rolesVue.roleToEdit.id}`
              rolesVue.resetEditForm(response.payload)
              window.setTimeout(() => {
                rolesVue.animated = ''
              }, 3000)
            })
            .catch(reason => {
              rolesVue.$refs.editModal.$el.scrollTop = 0
              ajaxErrorHandler({
                reason,
                errorText: 'Could not save role',
                errorName: 'editErrorMessage',
                vue: rolesVue,
                containerRef: 'editModal'
              })
            })
            .finally(() => {
              rolesVue.updating = false
            })
        })
        .catch(reason => {
          rolesVue.editErrorMessage = reason
          rolesVue.$refs.editModal.$el.scrollTop = 0
        })
    },
    /**
     * To current user roles and permissions.
     * @function
     * @param {object} updatedRole - The updated role
     * @returns {undefined}
     */
    refreshUserPermissions (updatedRole) {
      let index = this.$root.roles.findIndex(
        role => role.id === this.roleToEdit.id
      )

      if (index !== -1) {
        this.$root.roles[index] = updatedRole
        let newPermissions = {}
        this.$root.roles.forEach(role => {
          role.permissions.forEach(permission => {
            newPermissions[permission.name] = true
          })
        })
        this.setPermissions(newPermissions)
      }
    },
    /**
     * To check if the item data is valid before submitting to the backend.
     * @function
     * @returns {object} A promise that will validate the input form
     */
    validateNewRoleData () {
      var rolesVue = this
      return new Promise(function (resolve, reject) {
        if (!rolesVue.newRole.name.length) {
          reject('Name cannot be blank')
        }
        resolve('Hurray')
      })
    },
    /**
     * To check if the item data is valid before submitting to the backend.
     * @function
     * @returns {object} A promise that will validate the input form
     */
    validateEditedRoleData () {
      var rolesVue = this
      return new Promise(function (resolve, reject) {
        if (!rolesVue.roleToEdit.name.length) {
          reject('Name cannot be blank')
        }
        resolve('Hurray')
      })
    },
    /**
     * To open the delete modal
     * @function
     * @param {object} role - The role to delete
     * @returns {undefined}
     */
    openDeleteModal (role) {
      this.roleToDelete = { ...role }
      this.showDeleteModal = true
    },
    /**
     * To close the delete modal
     * @function
     * @returns {undefined}
     */
    closeDeleteModal () {
      this.showDeleteModal = false
      this.resetDeleteForm()
    },
    /**
     * To clear the delete form
     * @function
     * @returns {undefined}
     */
    resetDeleteForm () {
      this.roleToDelete = {}
    },
    /**
     * To clear the delete error
     * @function
     * @returns {undefined}
     */
    clearDeleteError () {
      this.deleteErrorMessage = ''
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showDeleteSuccess (payload = {}) {
      let title = 'Success'
      let text = 'The Role has been deleted'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The removal has been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    },
    /**
     * To delete a role
     * @function
     * @returns {undefined}
     */
    deleteRole () {
      this.deleting = true
      this.clearDeleteError()
      var rolesVue = this
      return RolesFunctions.deleteRole(rolesVue.roleToDelete)
        .then(response => {
          rolesVue.getRoles()
          rolesVue.closeDeleteModal()
          rolesVue.showDeleteSuccess(response.payload)
          rolesVue.resetDeleteForm()
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'Could not delete role',
            errorName: 'deleteErrorMessage',
            vue: rolesVue
          })
        })
        .finally(() => {
          rolesVue.deleting = false
        })
    },
    ...mapMutations({
      setPermissions: 'SET_PERMISSIONS'
    })
  }
}
</script>

<style scoped>
.animated {
  animation: listItemHighlight 1s 2 ease-in-out both;
}
.mt-element-list .list-news.mt-list-container ul > .mt-list-item {
  min-height: 80px;
}
.mt-element-list .list-news.mt-list-container ul > .mt-list-item:hover {
  background-color: white;
}
.module {
  font-weight: bold;
}
</style>
